import { lazy, Suspense, useEffect } from 'react'

import { TelemetrySourceProvider, TENANT_SOURCE } from './lib/api/source.tsx'
import { AuthProvider, useAuth } from './lib/auth/AuthContext.tsx'
import { navigate, setRouteBase, useRoutePath } from './lib/router.tsx'
import { Sidebar } from './components/layout/Sidebar.tsx'
import { PageHeader } from './components/layout/PageHeader.tsx'
import { EmptyState, LoadingRow } from './components/primitives/States.tsx'
import { ToastProvider } from './components/primitives/Toast.tsx'
import { TooltipProvider } from './components/primitives/Tooltip.tsx'
import { Account, ChangePassword } from './routes/Account.tsx'
import { Agents } from './routes/Agents.tsx'
import { AiFindings } from './routes/AiFindings.tsx'
import { Challenges } from './routes/Challenges.tsx'
import { Companies } from './routes/Companies.tsx'
import { Dashboard } from './routes/Dashboard.tsx'
import { Detections } from './routes/Detections.tsx'
import { Licenses } from './routes/Licenses.tsx'
import { Login } from './routes/Login.tsx'
import { Logs } from './routes/Logs.tsx'
import { Machines } from './routes/Machines.tsx'
import { Users } from './routes/Users.tsx'
import { Overview } from './routes/tenant/Overview.tsx'
import { TenantAgents } from './routes/tenant/TenantAgents.tsx'
import { TenantMachines } from './routes/tenant/TenantMachines.tsx'

/**
 * Prefixo da experiência de cliente. A Black Ghost fica na raiz (as rotas de
 * sempre); o cliente vive inteiro sob `/app`. Um endereço fora do prefixo da
 * própria experiência nunca renderiza a tela da outra — vira "não encontrado".
 */
export const TENANT_ROUTE_BASE = '/app'

/**
 * A página de processo traz o React Flow (~50 KB gz). Carregada sob demanda para
 * não pesar o console, que fica aberto o dia inteiro sem nunca abri-la.
 */
const ProcessPage = lazy(() =>
  import('./routes/Process.tsx').then((module) => ({ default: module.Process })),
)

/** Um `%` solto na barra de endereço não pode derrubar a casca inteira. */
function safeDecode(segment: string): string {
  try {
    return decodeURIComponent(segment).replace(/\/+$/, '')
  } catch {
    return segment
  }
}

export default function App() {
  return (
    <TooltipProvider>
      <ToastProvider>
        <AuthProvider>
          <Shell />
        </AuthProvider>
      </ToastProvider>
    </TooltipProvider>
  )
}

/**
 * A casca.
 *
 * Três estados, e a ordem importa: enquanto a sessão está sendo verificada não
 * se mostra o login **nem** o console. Mostrar o login já seria mentira — pode
 * haver um cookie válido de ontem, e o operador que digitasse a senha à toa
 * teria sido enganado por um estado de carregamento.
 */
function Shell() {
  const { status, scope, user } = useAuth()

  // O prefixo é decidido ANTES de qualquer tela renderizar: os construtores de
  // endereço e o `Link` o leem durante o render. Atribuição idempotente.
  setRouteBase(status === 'authenticated' && scope === 'tenant' ? TENANT_ROUTE_BASE : '')

  if (status === 'checking') return <Booting />
  if (status === 'anonymous') return <Login />

  // A experiência é escolhida por `scope` de /auth/me — nunca pelos papéis nem
  // pelo JWT. Um escopo desconhecido não ganha nenhuma das duas.
  if (scope !== 'platform' && scope !== 'tenant') return <UnknownScope />

  // Senha provisória: só a troca.
  if (user?.mustChangePassword === true) return <FirstAccessPassword />

  return (
    <div className="flex min-h-screen flex-col bg-canvas lg:flex-row">
      <Sidebar />
      <main className="flex min-h-0 min-w-0 flex-1 flex-col max-lg:min-h-screen lg:h-screen">
        {scope === 'platform' ? (
          <PlatformRoutes />
        ) : (
          // As telas compartilhadas (Detecções, Logs, Processo) leem de
          // /api/tenant em vez de /api/admin.
          <TelemetrySourceProvider source={TENANT_SOURCE}>
            <TenantRoutes />
          </TelemetrySourceProvider>
        )}
      </main>
    </div>
  )
}

/** Rotas compartilhadas com parâmetro: `/logs/:endpointId` e `/processes/:entityId`. */
function sharedRoute(path: string) {
  // /logs e /logs/:endpointId — o roteador de ~90 linhas não tem parâmetro de
  // rota, então o segmento é extraído aqui. O endpoint_id é o filtro fiel: um
  // agente sozinho fica abaixo do teto do quadro, então nada é amostrado (§2.1).
  if (path === '/analise-ia') return <AiFindings />
  if (path === '/logs') return <Logs />
  if (path.startsWith('/logs/')) {
    const endpointId = safeDecode(path.slice('/logs/'.length))
    return endpointId === '' ? <Logs /> : <Logs endpointId={endpointId} />
  }

  // /processes/:entityId — árvore + linha do tempo (§12). `key` remonta a tela
  // ao trocar de processo: expansões e páginas carregadas são de outro foco.
  if (path.startsWith('/processes/')) {
    const entityId = safeDecode(path.slice('/processes/'.length))
    if (entityId !== '') {
      return (
        <Suspense fallback={<LoadingRow label="Carregando…" />}>
          <ProcessPage key={entityId} entityId={entityId} />
        </Suspense>
      )
    }
  }

  return null
}

/**
 * Experiência do cliente, sob `/app`. Sem Empresas, Licenças, console global
 * nem comandos. Qualquer endereço fora de `/app` (uma rota da Black Ghost colada
 * na barra) cai em "não encontrado"; a raiz `/` leva à visão geral.
 */
function TenantRoutes() {
  const { path, raw } = useRoutePath()
  const { isTenantAdmin } = useAuth()

  // Quem entra pelo endereço raiz (o login acontece onde se estava) vai para a
  // visão geral. Qualquer outro endereço fora de `/app` não é desta experiência.
  const atRoot = path === null && raw === '/'
  useEffect(() => {
    if (atRoot) navigate('/', { replace: true })
  }, [atRoot])

  if (path === null) return atRoot ? null : <NotFound path={raw} />

  const shared = sharedRoute(path)
  if (shared !== null) return shared

  switch (path) {
    case '/':
      return <Overview />
    case '/deteccoes':
      return <Detections />
    case '/maquinas':
      return <TenantMachines />
    case '/agentes':
      return <TenantAgents />
    case '/conta':
      return <Account />
    case '/usuarios':
      // `tenant_user` não vê o item de menu, e o backend responde 403.
      return isTenantAdmin ? (
        <Users mode="tenant" />
      ) : (
        <Forbidden hint="Exige o papel tenant_admin." />
      )
    default:
      return <NotFound path={raw} />
  }
}

/** Experiência da Black Ghost: as rotas de sempre, na raiz. `/app/*` não é dela. */
function PlatformRoutes() {
  const { path, raw } = useRoutePath()
  const { isPlatformAdmin } = useAuth()

  if (path === null || path === TENANT_ROUTE_BASE || path.startsWith(`${TENANT_ROUTE_BASE}/`)) {
    return <NotFound path={raw} />
  }

  const shared = sharedRoute(path)
  if (shared !== null) return shared

  switch (path) {
    case '/':
      return <Dashboard />
    case '/deteccoes':
      return <Detections />
    case '/challenges':
      return <Challenges />
    case '/empresas':
      return <Companies />
    case '/maquinas':
      return <Machines />
    case '/licencas':
      return <Licenses />
    case '/agentes':
      return <Agents />
    case '/conta':
      return <Account />
    case '/usuarios':
      // O menu já esconde o item para quem não é administrador. Esta guarda é
      // para o endereço colado na barra — e a API responde 403 de qualquer
      // forma, porque autorização de verdade não acontece no cliente.
      return isPlatformAdmin ? (
        <Users mode="platform" />
      ) : (
        <Forbidden hint="Exige o papel platform_admin." />
      )
    default:
      return <NotFound path={raw} />
  }
}

function Booting() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-2 bg-canvas">
      <span className="font-mono text-base font-medium tracking-label text-fg">HERMES</span>
      <p role="status" className="text-sm text-fg-muted">
        Verificando a sessão…
      </p>
    </div>
  )
}

function Forbidden({ hint }: { hint: string }) {
  return (
    <>
      <PageHeader title="Sem permissão" />
      <EmptyState title="Você não tem permissão para ver esta tela." hint={hint} />
    </>
  )
}

function FirstAccessPassword() {
  const { signOut } = useAuth()
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-canvas px-4">
      <span className="font-mono text-base font-medium tracking-label text-fg">HERMES</span>
      <div className="w-full max-w-sm">
        <ChangePassword firstAccess />
      </div>
      <button type="button" className="text-sm text-fg-muted underline" onClick={() => void signOut()}>
        Sair
      </button>
    </div>
  )
}

/** Sessão válida, mas com um escopo que esta versão do Hermes não conhece. */
function UnknownScope() {
  const { signOut } = useAuth()
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-canvas px-4">
      <span className="font-mono text-base font-medium tracking-label text-fg">HERMES</span>
      <p className="max-w-md text-center text-sm text-fg-muted">
        Esta conta não tem um escopo de acesso reconhecido.
      </p>
      <button type="button" className="text-sm text-fg-muted underline" onClick={() => void signOut()}>
        Sair
      </button>
    </div>
  )
}

function NotFound({ path }: { path: string }) {
  return (
    <>
      <PageHeader title="Não encontrado" description={path} />
      <EmptyState title="Não há nada neste endereço." />
    </>
  )
}
